/* packages/ui/react/authorityModal.css — RUN RB. The authority viewer.
 *
 * 🔴 RUN CT — MOVED FROM `apps/main/client/src/screens/Clark/answer/`. It is no longer "over the
 * Clark thread" only: Research mounts the same component over its own workspace. Not one token or
 * one number below changed in the move — the parity Adam asked for is the SAME FILE rendering in
 * both apps, and a copy edited to match is the lookalike he explicitly refused.
 *
 * ⭐ EVERY TOKEN THIS FILE READS IS DEFINED IN BOTH APPS, RE-MEASURED IN RUN CT RATHER THAN
 * INHERITED. Eleven of the thirteen come from the generated `@citeclerk/ui/themes.css` (19-20
 * definitions each, one per theme), which steel imports at main.jsx:17. The other two —
 * `--cc-font-body` and `--cc-font-display` — have ZERO definitions in themes.css and are declared
 * per-app: apps/steel/client/src/styles/tokens.css:58-59 and apps/main/.../tokens.css:55-56, with
 * byte-identical values. Anyone re-checking this claim in themes.css alone will not find them.
 *
 * Built 1:1 against apps/main/ops/design/clark-authority-modal-v1.html and the extracted table in
 * apps/main/ops/design/MEASUREMENTS.md. Every layout number below is that file's.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * 🔴 NOTHING HERE SETS `backdrop-filter`, AND THAT IS DELIBERATE.
 * The blur belongs to `./Modal.css` (`.cc-modal-overlay { backdrop-filter: blur(6px) }`),
 * which this component imports rather than forks. The mockup flagged its own `--blur:6px` as a
 * stand-in for "the app's existing modal backdrop" (rule 5) — and the app's existing modal backdrop
 * is blur(6px), so the import costs no pixel and buys the guarantee: ONE blur value in the product.
 *
 * 🔴 THE `:has()` BLOCK IS THIS STYLESHEET REACHING THE PRIMITIVE, NOT THE PRIMITIVE FORKING.
 * `Modal` accepts no className, so there is no hook to put on `.cc-modal-card`. The primitive is a
 * 28rem centred dialog with a navy header and `text-align:center`; this modal is an 860px document.
 * Scoping the geometry to a card that CONTAINS `.cc-authmodal` adapts it here and leaves every
 * other modal in the product untouched. `Modal.css` (now this file's sibling) is not edited.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * 🔴 TOKENS ONLY — ops/enforce/run.mjs fails the build on a raw colour, and 19 themes disagree
 * with any value derived against four of them.
 *
 * ⚠️ THE `--cc-page` DEVIATION, WHICH IS THE ONE PLACE THIS FILE DOES NOT COPY THE MOCKUP.
 *
 * The mockup paints `.prov`, `.foot` and `.btn[disabled]` on `--page` with `--ink-muted` text. In
 * the STANDALONE file that reads correctly, because its fallback is `#E7ECF5` — a pale blue-grey.
 * In the app `page` is a different thing: contract.mjs calls it *"THE colored page (surface-0) —
 * the theme's identity color"*, and on Verdict it is `#A11B33`. RUN PC measured exactly this
 * mistake — a brief that said "move the stage to --cc-page", 4 contrast failures, and a wordmark
 * at 1:1 — and its finding was that the mockup's FALLBACK was the intent while its ROLE NAME was
 * the bug.
 *
 * 🔴 AND THE THEME CONTRACT SAYS SO INDEPENDENTLY. CONTRAST_PAIRS gates `inkMuted` against `card`
 * and `cardAlt` — and against `page` it gates only `textOnPage`. So muted ink on `--cc-page` is a
 * pairing NO theme is verified for, in all 19. `cardAlt` is defined as *"surface between card and
 * page (subtle panels ON cards)"*, which is precisely what a provenance block and a modal foot are,
 * and `ink`/`inkMuted`/`accentText` are all gated ≥4.5:1 against it.
 *
 * So: `--cc-card-alt` for those three, `--cc-page` nowhere. The mockup governs layout, and this is
 * a colour ROLE — which its own five-rule header hands to themes.css. Measured across all 19 in
 * ops/run-rb/out/contrast.json.
 *
 * ⚠️ AND A SECOND ROLE CORRECTION THE CONTRAST SWEEP FOUND, WHICH THE MOCKUP ALSO COULD NOT SHOW.
 * The mockup draws every border — the head/body rules AND the button edges — as one `--line`. The
 * app has two roles there, and the contract separates them for a reason: `hairline` is
 * "border-soft", ungated; `edge` is "border-strong — visible control edges (D-39)" and is gated
 * ≥3:1 against card/cardAlt/raised/hover in CONTRAST_PAIRS.
 *
 * MEASURED: `--cc-hairline` on `--cc-card` is **1.60–1.61:1 on all 19 themes**. That is correct for
 * a separator and wrong for a CONTROL, whose boundary is what tells a reader it is a control at all
 * (WCAG 1.4.11). So the three CONTROLS here — the buttons, the close box and the Esc key cap — take
 * `--cc-edge`; the structural rules between head, body and foot, and the provenance box, keep
 * `--cc-hairline`. One mockup line, two roles, and only one of them is a control.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/* ══ THE SHELL — the primitive's card, adapted to a document ══════════════════════════════════ */
.cc-modal-card:has(.cc-authmodal) {
  max-width: 860px;                 /* mockup .modal width:min(860px,100%) */
  max-height: min(86vh, 900px);
  border-radius: 14px;              /* --r-lg; the primitive's own default is 16 */
  border: 1px solid var(--cc-hairline);
  text-align: left;                 /* the primitive centres; a document does not */
  display: flex;
  flex-direction: column;
}
.cc-modal-card:has(.cc-authmodal) > .cc-modal-body {
  padding: 0;                       /* the head/body/foot carry their own padding */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;                    /* without this the inner scroller cannot shrink */
}

.cc-authmodal {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  color: var(--cc-ink);
  font-family: var(--cc-font-body);
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
}
.cc-authmodal-ic {
  width: 15px; height: 15px;
  stroke: currentColor; fill: none; stroke-width: 1.7;
  stroke-linecap: round; stroke-linejoin: round;
  display: block; flex: 0 0 auto;
}
.cc-authmodal-sp { margin-left: auto; }

/* ══ THE HEAD — paints at 0 ms; does not scroll ══════════════════════════════════════════════ */
.cc-authmodal-head { padding: 22px 26px 0; flex: 0 0 auto; }
.cc-authmodal-headtop {
  display: flex; align-items: flex-start; gap: 16px;
  padding-right: 44px;              /* clears the close button */
}
.cc-authmodal-kind {
  font-size: 10.5px; letter-spacing: .1em; text-transform: uppercase;
  font-weight: 600; color: var(--cc-ink-muted); margin: 0 0 7px;
}
.cc-authmodal-title {
  font-family: var(--cc-font-display);
  font-size: 27px; line-height: 1.2; font-weight: 700; margin: 0;
  color: var(--cc-ink);
}
.cc-authmodal-title.is-statute { font-size: 25px; }
.cc-authmodal-subtitle {
  font-family: var(--cc-font-display);
  font-size: 17px; font-style: italic; color: var(--cc-ink-muted); margin: 5px 0 0;
}

/* 🔴 THE CLOSE CONTROL PINS TO THE MODAL'S TRUE TOP-RIGHT CORNER — Adam's ruling, 2026-08-24.
   It is positioned against `.cc-modal-card`, which the shared primitive already sets
   `position:relative`, NOT against the head — so it holds when a long case name wraps. */
.cc-authmodal-x {
  position: absolute; top: 14px; right: 14px; z-index: 3;
  width: 32px; height: 32px;
  border: 1px solid var(--cc-edge);
  background: var(--cc-card);
  border-radius: 6px;
  color: var(--cc-ink-muted);
  cursor: pointer;
  display: grid; place-items: center;
  font-family: inherit;
}
.cc-authmodal-x:hover { background: var(--cc-card-hover); color: var(--cc-ink); }
.cc-authmodal-x .cc-authmodal-ic { width: 16px; height: 16px; }

.cc-authmodal-meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 14px;
  margin: 12px 0 0; font-size: 12.5px; color: var(--cc-ink-muted);
}
.cc-authmodal-meta .cc-authmodal-r { font-variant-numeric: tabular-nums; }
.cc-authmodal-meta i { font-style: normal; opacity: .45; }

/* ⭐ PROVENANCE — STATUTES ONLY. NOT TREATMENT. DO NOT DELETE.
   See the header for why this is `--cc-card-alt` and not the mockup's `--page`. */
.cc-authmodal-prov {
  margin: 14px 0 0; padding: 9px 12px;
  border: 1px solid var(--cc-hairline); border-radius: 6px;
  background: var(--cc-card-alt);
  font-size: 12px; line-height: 1.45; color: var(--cc-ink-muted);
}
.cc-authmodal-prov b { color: var(--cc-ink); font-weight: 600; }

.cc-authmodal-acts {
  display: flex; gap: 8px; flex-wrap: wrap;
  padding: 16px 0 15px; margin: 15px 0 0;
  border-top: 1px solid var(--cc-hairline);
}
.cc-authmodal-btn {
  border: 1px solid var(--cc-edge);
  background: var(--cc-card);
  color: var(--cc-ink);
  border-radius: 6px; padding: 8px 14px; font-size: 13px;
  font-family: inherit; cursor: pointer; text-decoration: none;
  display: inline-flex; align-items: center; gap: 7px;
}
.cc-authmodal-btn:hover { background: var(--cc-card-hover); }
/* JP: one filled button carries the prime voice ALONE. */
.cc-authmodal-btn.is-prime {
  background: var(--cc-accent-fill);
  color: var(--cc-on-accent);
  border-color: var(--cc-accent-fill);
}
.cc-authmodal-btn.is-prime:hover { background: var(--cc-accent-fill-hover); }
.cc-authmodal-btn[disabled] {
  opacity: 1;                        /* an opacity fade is what puts muted ink below 4.5:1 */
  color: var(--cc-ink-muted);
  background: var(--cc-card-alt);
  cursor: not-allowed;
}

/* ══ THE BODY — the only thing that scrolls ═════════════════════════════════════════════════ */
.cc-authmodal-body {
  padding: 20px 26px 26px;
  overflow: auto; flex: 1 1 auto; min-height: 0;
  border-top: 1px solid var(--cc-hairline);
}
.cc-authmodal-bodyhead {
  font-size: 10.5px; letter-spacing: .1em; text-transform: uppercase;
  font-weight: 600; color: var(--cc-ink-muted); margin: 0 0 14px;
}

.cc-authmodal-opinion { font-family: var(--cc-font-display); font-size: 16.5px; line-height: 1.65; }
.cc-authmodal-opinion p { margin: 0 0 1em; }

/* statute text — hanging indents carry the (a)(1)(A) structure */
.cc-authmodal-stat { font-family: var(--cc-font-display); font-size: 16px; line-height: 1.62; }
.cc-authmodal-stat p.is-lv1 { margin: 0 0 .85em; padding-left: 2.1em; text-indent: -2.1em; }
.cc-authmodal-stat p.is-lv2 { margin: 0 0 .85em; padding-left: 3.6em; text-indent: -1.5em; }
.cc-authmodal-stat p.is-lv3 { margin: 0 0 .85em; padding-left: 5.1em; text-indent: -1.5em; }
/* the markers are NOT bold — only tabular */
.cc-authmodal-stat b { font-weight: 400; font-variant-numeric: tabular-nums; }

.cc-authmodal-note {
  font-size: 12.5px; line-height: 1.5; color: var(--cc-ink-muted);
  margin: 0; font-family: var(--cc-font-body);
}
.cc-authmodal-opinion .cc-authmodal-note { margin-top: 1.2em; }

/* ══ THE LOADING STATE — the state that matters most ════════════════════════════════════════
   ⚠️ NO CENTRED SPINNER. At eight seconds a spinner reads as failure; skeleton lines in the shape
   of the content read as arriving. The head above is already real. */
.cc-authmodal-sk {
  display: block; height: 1em; border-radius: 3px; margin: 0 0 .82em;
  background: linear-gradient(90deg,
    var(--cc-card-alt) 0%, var(--cc-card-hover) 38%, var(--cc-card-alt) 76%);
  background-size: 280% 100%;
  animation: cc-authmodal-sh 1.5s ease-in-out infinite;
}
@keyframes cc-authmodal-sh {
  0%   { background-position: 120% 0; }
  100% { background-position: -40% 0; }
}
.cc-authmodal-sk.is-w95 { width: 95%; }
.cc-authmodal-sk.is-w88 { width: 88%; }
.cc-authmodal-sk.is-w74 { width: 74%; }
.cc-authmodal-sk.is-w60 { width: 60%; }
.cc-authmodal-sk.is-w40 { width: 40%; }

.cc-authmodal-loadnote {
  font-size: 12.5px; color: var(--cc-ink-muted); margin: 0 0 16px;
  display: flex; align-items: center; gap: 8px;
}
.cc-authmodal-dot {
  width: 7px; height: 7px; border-radius: 999px; flex: 0 0 auto;
  background: var(--cc-accent-text);
  animation: cc-authmodal-dp 1.1s ease-in-out infinite;
}
@keyframes cc-authmodal-dp {
  0%, 100% { opacity: .28; }
  50%      { opacity: 1; }
}

/* ⚠️ A reader who has asked for no motion gets none. The skeleton still reads as a skeleton —
   it keeps its shape and its mid-tone; only the sweep stops. */
@media (prefers-reduced-motion: reduce) {
  .cc-authmodal-sk  { animation: none; background: var(--cc-card-alt); }
  .cc-authmodal-dot { animation: none; opacity: .7; }
}

/* ══ THE FOOT ══════════════════════════════════════════════════════════════════════════════ */
.cc-authmodal-foot {
  flex: 0 0 auto;
  border-top: 1px solid var(--cc-hairline);
  padding: 11px 26px;
  background: var(--cc-card-alt);
  font-size: 11.5px; color: var(--cc-ink-muted);
  display: flex; align-items: center; gap: 14px;
}
.cc-authmodal-foot kbd {
  font: 11px var(--cc-font-body);
  border: 1px solid var(--cc-edge);
  background: var(--cc-card);
  border-radius: 4px; padding: 1px 5px; color: var(--cc-ink-muted);
}
